/**
 * Mob system — spawning, AI, combat, death.
 * Client-side for single-player. Will migrate to server for multiplayer.
 */

import type { WorldBlockAccess } from "@craftgaming/shared";
import { resolveCollision, computeKnockbackDirection } from "@craftgaming/shared";

// ─── Mob types & stats ───

export const MobType = {
  ZOMBIE: 0,
  SKELETON: 1,
  SPIDER: 2,
  PIG: 3,
  COW: 4,
  CHICKEN: 5,
} as const;
export type MobTypeValue = (typeof MobType)[keyof typeof MobType];

export const AiState = {
  IDLE: 0,
  WANDER: 1,
  CHASE: 2,
  ATTACK: 3,
  FLEE: 4,
} as const;

type MobStats = {
  maxHealth: number;
  speed: number;
  damage: number;
  attackCooldown: number;
  attackRange: number;
  aggroRange: number;
  xpDrop: number;
  width: number;
  height: number;
  /** Passive animals never attack; they flee when hurt. */
  passive?: boolean;
  /** Item dropped on death (meat) — picked up from the ground. */
  drop?: { itemId: number; min: number; max: number };
};

const MOB_STATS: Record<number, MobStats> = {
  [MobType.ZOMBIE]: {
    maxHealth: 20,
    speed: 2.5,
    damage: 3,
    attackCooldown: 1.2,
    attackRange: 2,
    aggroRange: 16,
    xpDrop: 25,
    width: 0.6,
    height: 1.8,
  },
  [MobType.SKELETON]: {
    maxHealth: 16,
    speed: 3.0,
    damage: 4,
    attackCooldown: 2.0,
    attackRange: 12,
    aggroRange: 20,
    xpDrop: 30,
    width: 0.5,
    height: 1.8,
  },
  [MobType.SPIDER]: {
    maxHealth: 14,
    speed: 4.0,
    damage: 2,
    attackCooldown: 0.8,
    attackRange: 2,
    aggroRange: 12,
    xpDrop: 20,
    width: 0.8,
    height: 0.7,
  },
  [MobType.PIG]: {
    maxHealth: 10,
    speed: 2.0,
    damage: 0,
    attackCooldown: 0,
    attackRange: 0,
    aggroRange: 0,
    xpDrop: 8,
    width: 0.7,
    height: 0.9,
    passive: true,
    drop: { itemId: 101 /* ItemType.PORKCHOP */, min: 1, max: 3 },
  },
  [MobType.COW]: {
    maxHealth: 10,
    speed: 1.8,
    damage: 0,
    attackCooldown: 0,
    attackRange: 0,
    aggroRange: 0,
    xpDrop: 8,
    width: 0.8,
    height: 1.3,
    passive: true,
    drop: { itemId: 102 /* ItemType.BEEF */, min: 1, max: 3 },
  },
  [MobType.CHICKEN]: {
    maxHealth: 4,
    speed: 1.6,
    damage: 0,
    attackCooldown: 0,
    attackRange: 0,
    aggroRange: 0,
    xpDrop: 5,
    width: 0.4,
    height: 0.6,
    passive: true,
    drop: { itemId: 103 /* ItemType.CHICKEN_MEAT */, min: 1, max: 1 },
  },
};

// ─── Mob entity ───

export type MobEntity = {
  id: number;
  type: MobTypeValue;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  yaw: number;
  health: number;
  maxHealth: number;
  state: number;
  stateTimer: number;
  attackTimer: number;
  wanderDir: { x: number; z: number };
  dead: boolean;
  deathTimer: number;
};

let nextMobId = 1;

// ─── Mob System ───

export type MobSystem = {
  mobs: MobEntity[];
  update: (
    dt: number,
    playerX: number,
    playerY: number,
    playerZ: number,
    isNight?: boolean
  ) => MobUpdateResult;
  getDeadMobs: () => MobEntity[];
  clearDead: () => void;
  dispose: () => void;
};

export type MobDamageEvent = {
  damage: number;
  mobId: number;
  mobType: MobTypeValue;
  knockbackDirX: number;
  knockbackDirZ: number;
};

/** Ranged mobs shoot visible projectiles instead of dealing instant damage. */
export type MobShootEvent = {
  mobId: number;
  damage: number;
  fromX: number;
  fromY: number;
  fromZ: number;
  dirX: number;
  dirY: number;
  dirZ: number;
};

export type MobUpdateResult = {
  damageEvents: MobDamageEvent[];
  shootEvents: MobShootEvent[];
};

const MAX_MOBS = 20;
const SPAWN_INTERVAL = 4; // seconds
const SPAWN_MIN_DIST = 20;
const SPAWN_MAX_DIST = 40;
const DESPAWN_DIST = 60;
const GRAVITY = 32;

export function createMobSystem(worldAccess: WorldBlockAccess): MobSystem {
  const mobs: MobEntity[] = [];
  let spawnTimer = 2; // first spawn after 2s

  function spawnMob(playerX: number, playerY: number, playerZ: number, isNight: boolean) {
    if (mobs.length >= MAX_MOBS) return;

    // Random position around player
    const angle = Math.random() * Math.PI * 2;
    const dist = SPAWN_MIN_DIST + Math.random() * (SPAWN_MAX_DIST - SPAWN_MIN_DIST);
    const sx = playerX + Math.cos(angle) * dist;
    const sz = playerZ + Math.sin(angle) * dist;

    // Find ground level (scan down from player height + 20)
    let sy = playerY + 20;
    let foundGround = false;
    for (let y = Math.floor(sy); y > 0; y--) {
      const block = worldAccess.getBlockAtWorld(Math.floor(sx), y, Math.floor(sz));
      if (block !== 0) { // not AIR
        sy = y + 1;
        foundGround = true;
        break;
      }
    }
    if (!foundGround) return;

    // Day = mostly passive animals; night = mostly hostile monsters
    const hostiles = [MobType.ZOMBIE, MobType.ZOMBIE, MobType.SKELETON, MobType.SPIDER];
    const animals = [MobType.PIG, MobType.COW, MobType.CHICKEN];
    const hostileChance = isNight ? 0.8 : 0.2;
    const pool = Math.random() < hostileChance ? hostiles : animals;
    const type = pool[Math.floor(Math.random() * pool.length)];
    const stats = MOB_STATS[type];

    const mob: MobEntity = {
      id: nextMobId++,
      type,
      x: sx,
      y: sy,
      z: sz,
      vx: 0,
      vy: 0,
      vz: 0,
      yaw: Math.random() * Math.PI * 2,
      health: stats.maxHealth,
      maxHealth: stats.maxHealth,
      state: AiState.IDLE,
      stateTimer: 1 + Math.random() * 3,
      attackTimer: 0,
      wanderDir: { x: 0, z: 0 },
      dead: false,
      deathTimer: 0,
    };

    mobs.push(mob);
  }

  function update(
    dt: number,
    playerX: number,
    playerY: number,
    playerZ: number,
    isNight = false
  ): MobUpdateResult {
    const damageEvents: MobDamageEvent[] = [];
    const shootEvents: MobShootEvent[] = [];

    // Spawning — a bit faster at night (more monsters)
    spawnTimer -= dt;
    if (spawnTimer <= 0) {
      spawnTimer = isNight ? SPAWN_INTERVAL * 0.6 : SPAWN_INTERVAL;
      spawnMob(playerX, playerY, playerZ, isNight);
    }

    for (let i = mobs.length - 1; i >= 0; i--) {
      const mob = mobs[i];
      const stats = MOB_STATS[mob.type];

      // Death animation
      if (mob.dead) {
        mob.deathTimer += dt;
        if (mob.deathTimer > 1.0) {
          mobs.splice(i, 1);
        }
        continue;
      }

      // Despawn if too far
      const pdx = mob.x - playerX;
      const pdz = mob.z - playerZ;
      const playerDist = Math.sqrt(pdx * pdx + pdz * pdz);
      if (playerDist > DESPAWN_DIST) {
        mobs.splice(i, 1);
        continue;
      }

      // AI state machine
      const dx = playerX - mob.x;
      const dy = playerY - mob.y;
      const dz = playerZ - mob.z;
      const distToPlayer = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const hDist = Math.sqrt(dx * dx + dz * dz);

      mob.stateTimer -= dt;
      mob.attackTimer = Math.max(0, mob.attackTimer - dt);

      // State transitions (passive animals never chase or attack)
      if (!stats.passive) {
        if (distToPlayer < stats.aggroRange && mob.state !== AiState.ATTACK) {
          mob.state = AiState.CHASE;
        }

        if (distToPlayer < stats.attackRange && mob.attackTimer <= 0) {
          mob.state = AiState.ATTACK;
        }

        if (distToPlayer > stats.aggroRange && mob.state === AiState.CHASE) {
          mob.state = AiState.WANDER;
          mob.stateTimer = 2 + Math.random() * 3;
          randomWanderDir(mob);
        }
      }

      // State behavior
      switch (mob.state) {
        case AiState.IDLE:
          mob.vx = 0;
          mob.vz = 0;
          if (mob.stateTimer <= 0) {
            mob.state = AiState.WANDER;
            mob.stateTimer = 2 + Math.random() * 3;
            randomWanderDir(mob);
          }
          break;

        case AiState.WANDER:
          mob.vx = mob.wanderDir.x * stats.speed * 0.5;
          mob.vz = mob.wanderDir.z * stats.speed * 0.5;
          mob.yaw = Math.atan2(-mob.vx, -mob.vz);
          if (mob.stateTimer <= 0) {
            mob.state = AiState.IDLE;
            mob.stateTimer = 1 + Math.random() * 2;
          }
          break;

        case AiState.CHASE: {
          if (hDist > 0.5) {
            const nx = dx / hDist;
            const nz = dz / hDist;
            mob.vx = nx * stats.speed;
            mob.vz = nz * stats.speed;
            mob.yaw = Math.atan2(-dx, -dz);
          }
          break;
        }

        case AiState.FLEE: {
          // Hurt animals run AWAY from the player for a while
          if (hDist > 0.01) {
            mob.vx = (-dx / hDist) * stats.speed * 1.8;
            mob.vz = (-dz / hDist) * stats.speed * 1.8;
            mob.yaw = Math.atan2(dx, dz);
          }
          if (mob.stateTimer <= 0) {
            mob.state = AiState.WANDER;
            mob.stateTimer = 2 + Math.random() * 3;
            randomWanderDir(mob);
          }
          break;
        }

        case AiState.ATTACK:
          mob.vx = 0;
          mob.vz = 0;
          if (mob.attackTimer <= 0 && distToPlayer < stats.attackRange) {
            if (mob.type === MobType.SKELETON) {
              // Ranged: shoot a visible, dodgeable arrow — never
              // instant damage from a distance.
              const eyeY = mob.y + stats.height * 0.85;
              const targetY = playerY + 1.2;
              const len = Math.max(0.001, distToPlayer);
              shootEvents.push({
                mobId: mob.id,
                damage: stats.damage,
                fromX: mob.x,
                fromY: eyeY,
                fromZ: mob.z,
                dirX: dx / len,
                dirY: (targetY - eyeY) / len,
                dirZ: dz / len,
              });
            } else {
              const kbDir = computeKnockbackDirection(
                { x: mob.x, y: mob.y, z: mob.z },
                { x: playerX, y: playerY, z: playerZ }
              );
              damageEvents.push({
                damage: stats.damage,
                mobId: mob.id,
                mobType: mob.type,
                knockbackDirX: kbDir.x,
                knockbackDirZ: kbDir.z,
              });
            }
            mob.attackTimer = stats.attackCooldown;
          }
          if (distToPlayer > stats.attackRange * 1.5) {
            mob.state = AiState.CHASE;
          }
          break;
      }

      // Physics: gravity
      mob.vy -= GRAVITY * dt;

      // Movement + collision (resolveCollision does vel*dt internally)
      const resolved = resolveCollision(
        mob.x, mob.y, mob.z,
        mob.vx, mob.vy, mob.vz,
        stats.width, stats.height,
        dt, worldAccess
      );
      mob.x = resolved.x;
      mob.y = resolved.y;
      mob.z = resolved.z;

      if (resolved.onGround) {
        mob.vy = 0;

        // Jump if chasing and blocked
        if (mob.state === AiState.CHASE) {
          const frontBlock = worldAccess.getBlockAtWorld(
            Math.floor(mob.x + (mob.vx > 0 ? 1 : -1) * 0.5),
            Math.floor(mob.y + 0.5),
            Math.floor(mob.z + (mob.vz > 0 ? 1 : -1) * 0.5)
          );
          if (frontBlock !== 0) {
            mob.vy = 8; // jump
          }
        }
      }
    }

    return { damageEvents, shootEvents };
  }

  function randomWanderDir(mob: MobEntity) {
    const angle = Math.random() * Math.PI * 2;
    mob.wanderDir.x = Math.cos(angle);
    mob.wanderDir.z = Math.sin(angle);
  }

  function getDeadMobs(): MobEntity[] {
    return mobs.filter((m) => m.dead);
  }

  function clearDead() {
    // handled in update loop
  }

  function dispose() {
    mobs.length = 0;
  }

  return { mobs, update, getDeadMobs, clearDead, dispose };
}

/**
 * Damage a mob. Returns true if mob died.
 * Passive animals flee when hurt.
 */
export function damageMob(mob: MobEntity, amount: number): boolean {
  mob.health -= amount;
  if (MOB_STATS[mob.type].passive && mob.health > 0) {
    mob.state = AiState.FLEE;
    mob.stateTimer = 4;
  }
  if (mob.health <= 0) {
    mob.health = 0;
    mob.dead = true;
    mob.deathTimer = 0;
    return true;
  }
  return false;
}

export function getMobStats(type: MobTypeValue): MobStats {
  return MOB_STATS[type];
}
